/**
 * Toast/Status Notification Module
 *
 * Handles toast notifications and status messages in the widget.
 */

import { STATUS_MESSAGE_DURATION } from '../../constants.js';

/**
 * Show a toast notification
 * @param {string} message - Toast message
 * @param {string} type - Toast type ('info', 'success', 'error', 'warning')
 * @param {Object} options - Options
 * @param {ShadowRoot|HTMLElement} options.container - Container to append toast to (shadowRoot or element)
 * @param {number} options.duration - Duration in ms (default 3000)
 * @returns {HTMLElement} The toast element
 */
export function showToast(message, type = 'info', options = {}) {
    const { container, duration = 3000 } = options;

    if (!container) {
        console.warn('[FyncallToast] No container provided for toast');
        return null;
    }

    // Check if toast container exists, create if not
    let toastContainer = container.querySelector('.fyncall-toast-container');
    if (!toastContainer) {
        toastContainer = document.createElement('div');
        toastContainer.className = 'fyncall-toast-container';
        container.appendChild(toastContainer);
    }

    const toast = document.createElement('div');
    toast.className = `fyncall-toast ${type}`;
    toast.textContent = message;

    toastContainer.appendChild(toast);

    // Auto-remove after duration
    setTimeout(() => {
        toast.classList.add('fade-out');
        setTimeout(() => toast.remove(), 300);
    }, duration);

    return toast;
}

/**
 * Show a status message in the status element
 * @param {string} message - Status message
 * @param {string} type - Status type ('info', 'success', 'error', 'warning')
 * @param {HTMLElement} statusElement - The status element to update
 * @param {number} autoClearDuration - Duration before auto-clearing (0 = no auto-clear)
 */
export function showStatus(message, type, statusElement, autoClearDuration = 0) {
    if (!statusElement) {
        console.warn('[FyncallToast] No status element provided');
        return;
    }

    statusElement.textContent = message;
    statusElement.className = 'show ' + type;

    // Auto-clear for success messages
    if (type === 'success' || autoClearDuration > 0) {
        const clearAfter = autoClearDuration || STATUS_MESSAGE_DURATION;
        setTimeout(() => {
            statusElement.className = '';
        }, clearAfter);
    }
}

/**
 * Clear the status message
 * @param {HTMLElement} statusElement - The status element to clear
 */
export function clearStatus(statusElement) {
    if (statusElement) {
        statusElement.textContent = '';
        statusElement.className = '';
    }
}

/**
 * Create toast container styles (inject once)
 * @returns {string} CSS styles
 */
export function getToastStyles() {
    return `
        .fyncall-toast-container {
            position: fixed;
            top: 20px;
            right: 20px;
            z-index: 10000;
            display: flex;
            flex-direction: column;
            gap: 10px;
            pointer-events: none;
        }
        .fyncall-toast {
            padding: 12px 20px;
            border-radius: 8px;
            color: white;
            font-size: 14px;
            font-weight: 500;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
            animation: fyncall-toast-in 0.3s ease;
            pointer-events: auto;
            max-width: 300px;
        }
        .fyncall-toast.info {
            background: #3b82f6;
        }
        .fyncall-toast.success {
            background: #22c55e;
        }
        .fyncall-toast.error {
            background: #ef4444;
        }
        .fyncall-toast.warning {
            background: #f59e0b;
        }
        .fyncall-toast.fade-out {
            animation: fyncall-toast-out 0.3s ease forwards;
        }
        @keyframes fyncall-toast-in {
            from {
                opacity: 0;
                transform: translateX(20px);
            }
            to {
                opacity: 1;
                transform: translateX(0);
            }
        }
        @keyframes fyncall-toast-out {
            from {
                opacity: 1;
                transform: translateX(0);
            }
            to {
                opacity: 0;
                transform: translateX(20px);
            }
        }
    `;
}

/**
 * Inject toast styles into container
 * @param {ShadowRoot|HTMLElement} container - Container to inject styles into
 */
export function injectToastStyles(container) {
    if (!container) return;

    const styleId = 'fyncall-toast-styles';
    if (container.querySelector(`#${styleId}`)) return;

    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = getToastStyles();
    container.appendChild(style);
}
